import { useState, useEffect, useMemo, useCallback } from 'react';
import BetKeypad from './BetKeypad';
import DrawTimer from './DrawTimer';
import KeypadHeader from './KeypadHeader';
import QuickActions from './QuickActions';
import { walletApi } from '../../api/wallet';
import { useAuth } from '../../context/AuthContext';
import { gamesApi } from '../../api/games';
import dayjs from 'dayjs';
import toast, { Toaster } from 'react-hot-toast';
import getGameType from '../../utils/games';
import TicketPopup from '../../components/Popup/TicketPopup';
import PrizeBanners from './PrizeBanners';
import {
  getAllBets,
  saveBets,
  addBet,
  removeBet,
  getBetsByGame,
  calculateBetTotal,
  clearBets,
} from '../../utils/betStorage';
import { useWebSocket } from '../../context/WebSocketContext';
import { useLocation } from '../../hooks/useLocation';

export default function Bets() {
  const { tokens, user } = useAuth();
  const { refreshWalletBalance } = useWebSocket();
  const { logAction } = useLocation();
  const [wallet, setWallet] = useState({ balance: 0 });
  const [results, setResults] = useState([]);
  const [nextDrawTime, setNextDrawTime] = useState(null);
  const [gameSettings, setGameSettings] = useState([]);
  const [selectedGameSettings, setSelectedGameSettings] = useState(null);
  const [game, setGame] = useState(null);
  const [gameType, setGameType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userGames, setUserGames] = useState([]);
  const [selectedGameName, setSelectedGameName] = useState('');
  const [selectedGame, setSelectedGame] = useState(null);
  const [betData, setBetData] = useState([]);
  const [bets, setBets] = useState([]);
  const [bet, setBet] = useState({
    combination: '',
    combination2: '',
    combination3: '',
    straight: '',
    ramble: '',
  });
  const [activeDraw, setActiveDraw] = useState(null);
  const [currentDrawTime, setCurrentDrawTime] = useState(null);
  const [activeDrawTime, setActiveDrawTime] = useState(null);
  const [isDrawTimeCutoff, setIsDrawTimeCutoff] = useState([]);
  const [cutOffTimes, setCutOffTimes] = useState([]);
  const [drawTimes, setDrawTimes] = useState([]);
  const [betTotal, setBetTotal] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [showTicket, setShowTicket] = useState(false);
  const [showAllBets, setShowAllBets] = useState(true);

  /* Fetch all initial data in parallel */
  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const fetchAllData = async () => {
      if (!tokens?.access?.token || !user?.id) return;

      try {
        // Fetch all data in parallel
        const [
          walletResponse,
          gameSettingsResponse,
          userGamesResponse,
          resultsResponse,
        ] = await Promise.all([
          walletApi.getWallet(tokens.access.token, user.id),
          gamesApi.getAllGames(tokens.access.token),
          gamesApi.getUserGames(tokens.access.token, user.id),
          gamesApi.getAllResults(tokens.access.token),
        ]);

        if (!mounted) return;

        // Process all responses
        setWallet(walletResponse);
        setGameSettings(gameSettingsResponse.data);
        setUserGames(userGamesResponse);

        // Process results with memoization
        const digitStyles = [
          {
            color: '[#0E64D1]',
            gradient: `conic-gradient(from 0deg, #0E64D1 0deg 40deg, white 40deg 90deg, #0E64D1 90deg 130deg, white 130deg 180deg, #0E64D1 180deg 220deg, white 220deg 270deg, #0E64D1 270deg 310deg, white 310deg 360deg)`,
          },
          {
            color: 'yellow-400',
            gradient: `conic-gradient(from 0deg, #0ED173 0deg 40deg, white 40deg 90deg, #0ED173 90deg 130deg, white 130deg 180deg, #0ED173 180deg 220deg, white 220deg 270deg, #0ED173 270deg 310deg, white 310deg 360deg)`,
          },
          {
            color: 'red-600',
            gradient: `conic-gradient(from 0deg, #D92000 0deg 40deg, white 40deg 90deg, #D92000 90deg 130deg, white 130deg 180deg, #D92000 180deg 220deg, white 220deg 270deg, #D92000 270deg 310deg, white 310deg 360deg)`,
          },
        ];

        const transformedResults = resultsResponse.map((result) => ({
          time: result.drawTime,
          gameId: result.gameId,
          numbers: result.combinations.split('').map((digit, index) => ({
            num: digit,
            color: digitStyles[index % digitStyles.length].color,
            gradient: digitStyles[index % digitStyles.length].gradient,
          })),
        }));

        setResults(transformedResults);

        // Store user games in session storage for dropdown access
        sessionStorage.setItem('user_games', JSON.stringify(userGamesResponse));

        // Continue with game-specific fetching
        const id = window.location.pathname.split('/').pop();
        if (id) {
          fetchGameById(id, gameSettingsResponse.data);
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };

    const fetchGameById = async (id, gameSettingsData) => {
      try {
        const gameData = await gamesApi.getGame(tokens.access.token, id);
        if (!mounted) return;

        setGame(gameData);

        // Process game settings
        const gameSetting = gameSettingsData.find(
          (setting) => setting.id === gameData.game_settings_id
        );
        setSelectedGameSettings(gameSetting);

        const image = gameSetting?.otherSettings?.image;
        const newGameType = getGameType(image);
        setGameType(newGameType);

        // Handle both single and multiple draw times
        const times = gameSetting?.otherSettings?.cutOffTime;
        const drawTimes = gameSetting?.otherSettings?.drawTime;

        // Convert single values to arrays if needed
        const cutOffTimesArray = Array.isArray(times) ? times : [times];
        const drawTimesArray = Array.isArray(drawTimes)
          ? drawTimes
          : [drawTimes];

        // Sort the drawTimesArray in chronological order
        const sortedDrawTimesArray = [...drawTimesArray].sort((a, b) => {
          // Convert time strings to minutes since midnight for comparison
          const timeToMinutes = (time) => {
            const [hours, minutes] = time.split(':').map(Number);
            return hours * 60 + minutes;
          };
          return timeToMinutes(a) - timeToMinutes(b);
        });

        console.log('drawTimesArray', sortedDrawTimesArray);

        setCutOffTimes(cutOffTimesArray);
        setDrawTimes(sortedDrawTimesArray);

        // Set initial draw time based on current time
        const now = dayjs();
        const today = dayjs().format('YYYY-MM-DD');

        // Find the next available draw time
        const nextDrawTime =
          sortedDrawTimesArray.find((time) => {
            const drawTime = dayjs(`${today} ${time}`);
            return drawTime.isAfter(now);
          }) || sortedDrawTimesArray[0];

        setActiveDraw(
          cutOffTimesArray[sortedDrawTimesArray.indexOf(nextDrawTime)]
        );
        setCurrentDrawTime(nextDrawTime);
        setActiveDrawTime(nextDrawTime);

        // Set next draw time for timer display
        const sortedDrawTimes = sortedDrawTimesArray
          .map((time) => dayjs(`${today} ${time}`))
          .sort((a, b) => a.diff(b));

        const upcomingDraw =
          sortedDrawTimes.find((time) => time.isAfter(now)) ||
          sortedDrawTimes[0];
        setNextDrawTime(upcomingDraw.format('HH:mm:ss'));

        // Handle cutoff checks
        if (cutOffTimesArray.length > 1) {
          const cutoffArray = cutOffTimesArray.map((_, index) =>
            isCutoffTime(index)
          );
          setIsDrawTimeCutoff(cutoffArray);
        }

        // Load game-specific bets
        const storedBets = getBetsByGame(gameData.id.toString());
        setBets(storedBets);
        setBetData(storedBets);

        // Calculate total for current game's bets
        const totalAmount = calculateBetTotal(gameData.id.toString());
        setBetTotal(totalAmount);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching game:', error);
        setLoading(false);
        // Redirect on error
        const page = error.status === 500 ? '/login' : '/';
        window.location.href = page;
      }
    };

    fetchAllData();

    return () => {
      mounted = false;
    };
  }, [tokens?.access?.token, user?.id]);

  // Memoize isCutoffTime to avoid recalculations
  const isCutoffTime = useCallback(
    (index) => {
      if (
        !selectedGameSettings ||
        !selectedGameSettings.otherSettings ||
        !selectedGameSettings.otherSettings.cutOffTime ||
        !selectedGameSettings.otherSettings.cutOffTime[index]
      ) {
        return false;
      }

      const cutOffTime =
        selectedGameSettings.otherSettings.cutOffTime[index].split(':');
      const today = dayjs().format('YYYY-MM-DD');
      const targetTime = dayjs(`${today} ${cutOffTime[0]}:${cutOffTime[1]}`);
      const currentTime = dayjs();
      return currentTime.isAfter(targetTime) || currentTime.isSame(targetTime);
    },
    [selectedGameSettings]
  );

  useEffect(() => {
    if (userGames.length > 0) {
      const id = window.location.pathname.split('/').pop();
      const userGame = userGames.find((game) => game.id === parseInt(id, 10));
      setSelectedGameName(userGame.name);
      setSelectedGame(userGame);
    }
  }, [userGames]);

  useEffect(() => {
    if (selectedGameSettings && selectedGameSettings.otherSettings) {
      const now = dayjs();
      const today = dayjs().format('YYYY-MM-DD');

      // Check cutoff times when settings are loaded
      if (
        selectedGameSettings.otherSettings.cutOffTime &&
        selectedGameSettings.otherSettings.drawTime
      ) {
        const drawTimesArray = selectedGameSettings.otherSettings.drawTime;
        const cutoffStatuses = drawTimesArray.map((_, index) =>
          isCutoffTime(index)
        );
        setIsDrawTimeCutoff(cutoffStatuses);
      }

      const sortedDrawTimes = selectedGameSettings.otherSettings.drawTime
        .map((time) => dayjs(`${today} ${time}`))
        .sort((a, b) => a.diff(b));

      const upcomingDraw =
        sortedDrawTimes.find((time) => time.isAfter(now)) || sortedDrawTimes[0];
      setNextDrawTime(upcomingDraw.format('HH:mm:ss'));
    }
  }, [selectedGameSettings, isCutoffTime]);

  // Load bets from persistent storage when game changes
  useEffect(() => {
    if (game?.id) {
      // Load game-specific bets from storage
      const storedBets = getBetsByGame(game.id.toString());
      setBets(storedBets);
      setBetData(storedBets);

      // Calculate total for current game's bets
      const totalAmount = calculateBetTotal(game.id.toString());
      setBetTotal(totalAmount);
    }
  }, [game]);

  /* Check if all data is loaded */
  useEffect(() => {
    if (wallet && gameSettings.length > 0 && game) {
      setLoading(false);
    }
  }, [wallet, results, gameSettings, game]);

  const onSelectedDrawTime = (time) => {
    console.log('Selected draw time:', time);
    console.log('Game type:', gameType);
    setCurrentDrawTime(time);

    // Update activeDrawTime as well to maintain consistency
    setActiveDrawTime(time);

    // Make sure the cutoff time calculation has the updated date and time
    const drawTimeIndex = drawTimes.findIndex((dt) => dt === time);
    if (drawTimeIndex !== -1) {
      const isAfterCutoff = isCutoffTime(drawTimeIndex);
      if (isAfterCutoff) {
        toast.error('Selected time is after cutoff time');
      }
    }
  };

  const pad2 = (num) => num.toString().padStart(2, '0');

  // Memoize bet operations to reduce renders
  const onAddBets = useCallback(
    (bets, skipToast = false) => {
      const drawTimeIndex = results.findIndex(
        (result) => result.time === bets.time
      );
      const newBetAmount = (+bets.straight || 0) + (+bets.ramble || 0);
      const newTotal = betTotal + newBetAmount;

      if (gameType === 'PICK3') {
        const combinationArray = [
          bets.combination,
          bets.combination2,
          bets.combination3,
        ];
        const uniqueCombination = [...new Set(combinationArray)];
        if (uniqueCombination.length !== combinationArray.length) {
          toast.error('Invalid combination');
          return;
        }
      }

      if (isCutoffTime(drawTimeIndex)) {
        toast.error('Cutoff time reached');
      } else {
        const betTypes = ['straight', 'ramble'];
        betTypes.forEach((betType) => {
          let loop = 1;
          if (!+bets[betType]) {
            return;
          }

          let winAmount = +selectedGame.winRate;
          let combiCount = [...new Set(bets.combination.split(''))].length;

          if (['Swertres', 'STL', 'LAST3'].includes(gameType)) {
            if (combiCount === 2) {
              winAmount = +selectedGame.doubleWin;
            } else if (combiCount === 1) {
              winAmount = +selectedGame.tripleWin;
            }

            if (betType === 'ramble') {
              switch (combiCount) {
                case 2:
                  winAmount = winAmount / 3;
                  break;
                case 3:
                  winAmount = winAmount / 6;
                  break;
                default:
                  break;
              }
            }
          } else if (
            ['2D', 'LAST2', 'S2'].includes(gameType) &&
            betType === 'ramble'
          ) {
            if (gameType === 'S2') {
              // For S2 ramble, create two bets: original and reversed
              const originalCombination = bets.combination;
              const reversedCombination = bets.combination
                .split('')
                .reverse()
                .join('');
              const rambleAmount = bets.ramble / 2;
              // First bet: original
              const betDataOriginal = {
                ...bets,
                combination: originalCombination,
                straight: rambleAmount,
                ramble: bets.ramble,
                drawTime: currentDrawTime,
                date: dayjs().format('YYYY-MM-DD'),
                gameId: game?.id.toString(),
                gameSettingsId: game?.game_settings_id,
                userId: user?.id,
                winAmount: winAmount * rambleAmount,
                bettingOption: 'target',
                amount: rambleAmount,
                gameType,
                gameName: selectedGameName,
              };
              setBets((prevBets) => [...prevBets, betDataOriginal]);
              let updatedBets = addBet(betDataOriginal);
              setBetData(updatedBets);
              // Second bet: reversed
              const betDataReversed = {
                ...bets,
                combination: reversedCombination,
                straight: rambleAmount,
                ramble: bets.ramble,
                drawTime: currentDrawTime,
                date: dayjs().format('YYYY-MM-DD'),
                gameId: game?.id.toString(),
                gameSettingsId: game?.game_settings_id,
                userId: user?.id,
                winAmount: winAmount * rambleAmount,
                bettingOption: 'target',
                amount: rambleAmount,
                gameType,
                gameName: selectedGameName,
              };
              setBets((prevBets) => [...prevBets, betDataReversed]);
              updatedBets = addBet(betDataReversed);
              setBetData(updatedBets);
              return; // Skip the rest of the loop logic for S2 ramble
            } else if (gameType === '2D') {
              // For 2D ramble, create two bets: original and reversed (e.g., '12-21' and '21-12')
              const originalCombination = bets.combination;
              let reversedCombination = bets.combination;
              if (bets.combination.includes('-')) {
                const parts = bets.combination.split('-');
                if (parts.length === 2) {
                  reversedCombination = `${parts[1]}-${parts[0]}`;
                }
              }
              const rambleAmount = bets.ramble / 2;
              // First bet: original
              const betDataOriginal = {
                ...bets,
                combination: originalCombination,
                straight: rambleAmount,
                ramble: bets.ramble,
                drawTime: currentDrawTime,
                date: dayjs().format('YYYY-MM-DD'),
                gameId: game?.id.toString(),
                gameSettingsId: game?.game_settings_id,
                userId: user?.id,
                winAmount: winAmount * rambleAmount,
                bettingOption: 'target',
                amount: rambleAmount,
                gameType,
                gameName: selectedGameName,
              };
              setBets((prevBets) => [...prevBets, betDataOriginal]);
              let updatedBets = addBet(betDataOriginal);
              setBetData(updatedBets);
              // Second bet: reversed
              const betDataReversed = {
                ...bets,
                combination: reversedCombination,
                straight: rambleAmount,
                ramble: bets.ramble,
                drawTime: currentDrawTime,
                date: dayjs().format('YYYY-MM-DD'),
                gameId: game?.id.toString(),
                gameSettingsId: game?.game_settings_id,
                userId: user?.id,
                winAmount: winAmount * rambleAmount,
                bettingOption: 'target',
                amount: rambleAmount,
                gameType,
                gameName: selectedGameName,
              };
              setBets((prevBets) => [...prevBets, betDataReversed]);
              updatedBets = addBet(betDataReversed);
              setBetData(updatedBets);
              return; // Skip the rest of the loop logic for 2D ramble
            } else if (gameType === 'LAST2') {
              const originalCombination = bets.combination;
              const reversedCombination = bets.combination
                .split('')
                .reverse()
                .join('');
              const rambleAmount = bets.ramble / 2;
              // First bet: original
              const betDataOriginal = {
                ...bets,
                combination: originalCombination,
                straight: rambleAmount,
                ramble: bets.ramble,
                drawTime: currentDrawTime,
                date: dayjs().format('YYYY-MM-DD'),
                gameId: game?.id.toString(),
                gameSettingsId: game?.game_settings_id,
                userId: user?.id,
                winAmount: winAmount * rambleAmount,
                bettingOption: 'target',
                amount: rambleAmount,
                gameType,
                gameName: selectedGameName,
              };
              setBets((prevBets) => [...prevBets, betDataOriginal]);
              let updatedBets = addBet(betDataOriginal);
              setBetData(updatedBets);
              // Second bet: reversed
              const betDataReversed = {
                ...bets,
                combination: reversedCombination,
                straight: rambleAmount,
                ramble: bets.ramble,
                drawTime: currentDrawTime,
                date: dayjs().format('YYYY-MM-DD'),
                gameId: game?.id.toString(),
                gameSettingsId: game?.game_settings_id,
                userId: user?.id,
                winAmount: winAmount * rambleAmount,
                bettingOption: 'target',
                amount: rambleAmount,
                gameType,
                gameName: selectedGameName,
              };
              setBets((prevBets) => [...prevBets, betDataReversed]);
              updatedBets = addBet(betDataReversed);
              setBetData(updatedBets);
              return; // Skip the rest of the loop logic for Last2 ramble
            } else {
              loop = 2;
              bets.straight = bets.ramble / 2;
              betType = 'straight';
            }
          }

          for (let i = 0; i < loop; i++) {
            let combination = bets.combination;
            let combination2 = '';
            let combination3 = '';

            if (gameType === 'PICK3') {
              combination2 = bets.combination2 || '';
              combination3 = bets.combination3 || '';
              combination = `${pad2(bets.combination)}-${pad2(
                combination2
              )}-${pad2(combination3)}`;
            }

            const bettingOption = betType === 'straight' ? 'target' : 'ramble';
            const betData = {
              combination,
              combination2,
              combination3,
              straight: bets.straight,
              ramble: bets.ramble,
              drawTime: currentDrawTime,
              date: dayjs().format('YYYY-MM-DD'),
              gameId: game?.id.toString(),
              gameSettingsId: game?.game_settings_id,
              userId: user?.id,
              winAmount: winAmount * +bets[betType],
              bettingOption,
              amount: +bets[betType],
              gameType, // Add game type for display in cart
              gameName: selectedGameName, // Add game name for display in cart
              rambleAmount:
                betType === 'ramble' ? bets?.rambleAmount : undefined,
            };

            // Add bet to local state for current game display
            setBets((prevBets) => [...prevBets, betData]);

            // Add bet to persistent storage (this now updates localStorage immediately)
            const updatedBets = addBet(betData);

            // Update betData state with all bets immediately after each bet is added
            setBetData(updatedBets);
          }
        });

        // Update total
        setBetTotal(newTotal);
        if (!skipToast) {
          toast.success('Bet added successfully');
        }
      }
    },
    [results, betTotal, game, currentDrawTime, selectedGameName, isCutoffTime]
  );

  const handleSubmitBets = useCallback(async () => {
    try {
      setLoading(true); // Show loading state during submission

      // Get all bets, not just the current game
      const allBets = getAllBets();

      // Check if there are any bets to submit
      if (allBets.length === 0) {
        toast.error('No bets to submit');
        setLoading(false);
        return;
      }

      let isMultiGame = false;
      if (allBets.length > 1) {
        const uniqueGameIds = [...new Set(allBets.map((bet) => bet.gameId))];
        isMultiGame = uniqueGameIds.length > 1;
      } else {
        isMultiGame = false;
      }

      // Determine transaction drawTime from bets
      // Use the earliest drawTime from all bets as the transaction drawTime
      const transactionDrawTime =
        allBets.reduce((earliest, bet) => {
          if (!earliest) return bet.drawTime;

          // Convert time strings to minutes since midnight for comparison
          const timeToMinutes = (timeStr) => {
            const [hours, minutes] = timeStr.split(':').map(Number);
            return hours * 60 + minutes;
          };

          const currentTimeMinutes = timeToMinutes(bet.drawTime);
          const earliestTimeMinutes = timeToMinutes(earliest);

          return currentTimeMinutes < earliestTimeMinutes
            ? bet.drawTime
            : earliest;
        }, null) || activeDrawTime; // Fallback to activeDrawTime if no bets have drawTime

      // Create payload with all bets - use memoization to prevent unnecessary recalculations
      const payload = {
        totalAmount: calculateBetTotal(), // Calculate total for all bets
        bets: allBets,
        // Use the earliest drawTime from bets for the transaction
        drawTime: transactionDrawTime,
        date: dayjs().format('YYYY-MM-DD'), // Add today's date
        // These fields will be used for display purposes but don't affect processing
        // when multiple games are included in bets array
        gameId: game?.id.toString(),
        gameSettingsId: game?.game_settings_id,
        gameName: selectedGameName,
        // Add flag to indicate this is a multi-game transaction
        isMultiGame: isMultiGame,
      };

      const response = await gamesApi.createTransaction(
        tokens.access.token,
        payload
      );

      if (response.status === 201) {
        // Log bet action with location
        logAction('bet', {
          betAmount: calculateBetTotal(),
          gameId: game?.id.toString(),
          gameName: selectedGameName,
        }).catch((error) => {
          console.warn('Failed to log bet location:', error);
          // Continue even if location logging fails
        });

        // Clear all bets from storage first to prevent any chance of duplicate submission
        clearBets();

        // Then update UI and show success message
        setTransaction(response.data);
        setShowTicket(true);

        // Update local state
        setBets([]);
        setBetData([]);
        setBetTotal(0);

        toast.success('All bets submitted successfully');
        refreshWalletBalance();
        window.dispatchEvent(new Event('walletRefresh'));
      } else {
        toast.error('Failed to submit bets');
      }
      setShowSummary(false); // Close the summary popup
    } catch (error) {
      console.error('Submit bets error:', error);
    } finally {
      setLoading(false); // Always hide loading state
    }
  }, [
    activeDrawTime,
    game,
    selectedGameName,
    tokens,
    refreshWalletBalance,
    logAction,
  ]);

  const onRemoveBet = (betId) => {
    // Remove the bet from persistent storage
    const updatedBets = removeBet(betId);

    // Update state with all bets, not just current game bets
    setBetData(updatedBets);

    // Filter current game bets for the game-specific UI
    const currentGameBets = getBetsByGame(game?.id.toString());
    setBets(currentGameBets);

    // Update total to reflect all bets in cart
    setBetTotal(calculateBetTotal());
  };

  // Function to clear all bets
  const handleClearAllBets = () => {
    // Clear all bets from storage
    clearBets();

    // Update states
    setBets([]);
    setBetData([]);
    setBetTotal(0);

    // Close the summary popup
    setShowSummary(false);

    // Show success message
    toast.success('All bets cleared successfully');
  };

  // Add this effect after the other useEffect hooks
  useEffect(() => {
    if (game) {
      // When game changes, update the bets display for the current game
      const currentGameBets = getBetsByGame(game.id.toString());
      setBets(currentGameBets);

      // Keep the full bet data for the cart popup
      const allBets = getAllBets();
      setBetData(allBets);

      // Update total to reflect all bets in cart
      setBetTotal(calculateBetTotal());
    }
  }, [game]);

  // Add this useEffect at the beginning of the component
  useEffect(() => {
    // Initialize the betData state with all bets on first load
    const allBets = getAllBets();
    if (allBets.length > 0) {
      setBetData(allBets);
      setBetTotal(calculateBetTotal());
    }
  }, []);

  // Update cutoff statuses every minute
  useEffect(() => {
    if (
      !selectedGameSettings ||
      !selectedGameSettings.otherSettings ||
      !selectedGameSettings.otherSettings.drawTime
    ) {
      return;
    }

    // Initial check
    const drawTimesArray = selectedGameSettings.otherSettings.drawTime;
    const cutoffStatuses = drawTimesArray.map((_, index) =>
      isCutoffTime(index)
    );
    setIsDrawTimeCutoff(cutoffStatuses);

    // Set up interval to check every minute
    const interval = setInterval(() => {
      const updatedStatuses = drawTimesArray.map((_, index) =>
        isCutoffTime(index)
      );
      setIsDrawTimeCutoff(updatedStatuses);
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [selectedGameSettings, isCutoffTime]);

  return (
    <div className='min-h-screen relative overflow-auto bg-[#1e3a8a] p-4 font-poppins bet-page'>
      <TicketPopup
        isOpen={showTicket}
        onClose={() => setShowTicket(false)}
        transaction={transaction}
      />
      <KeypadHeader
        balance={wallet.balance}
        selectedGameName={selectedGameName}
        betData={betData}
        betTotal={betTotal}
        handleSubmit={handleSubmitBets}
        showSummary={showSummary}
        setShowSummary={setShowSummary}
        onRemoveBet={onRemoveBet}
        clearAllBets={handleClearAllBets}
      />
      <PrizeBanners selectedGame={selectedGame} />
      <DrawTimer
        drawTime={nextDrawTime}
        drawDate={dayjs().format('ddd, D-MMMM-YY')}
      />
      <QuickActions />
      <BetKeypad
        drawTimes={results}
        gameSettings={selectedGameSettings}
        onAddBets={onAddBets}
        onSelectedDrawTime={onSelectedDrawTime}
        gameType={gameType}
        activeDrawTime={activeDrawTime}
        game={game}
        isDrawTimeCutoff={isDrawTimeCutoff}
        cutOffTimes={cutOffTimes}
      />
      {/* <Toaster /> */}

      {loading && (
        <div className='loading-overlay'>
          <div className='loading-text'>Loading...</div>
        </div>
      )}
    </div>
  );
}
